import { Button, buttonClasses, Callout, Skeleton } from '@fruuit/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Building2 } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router'
import { errorMessage } from '../api/client'
import { useApiMutation } from '../api/hooks'
import { meQueryKey, signOut, useMe } from '../api/session'
import { acceptInvite, useInviteLookup } from '../features/auth/api'
import { formatDate, ROLE_LABELS } from '../features/auth/format'
import { useTitle } from '../lib/useTitle'
import { AuthHeading, AuthLayout } from './AuthLayout'
import { SignInOptions } from './SignInOptions'

const ENDED: Record<string, { title: string; body: string }> = {
  accepted: {
    title: 'This invitation was already used',
    body: 'Sign in with the email address it was sent to.',
  },
  expired: {
    title: 'This invitation has expired',
    body: 'Invitations work for seven days. Ask your Fruuit Consulting contact to send a new one.',
  },
  revoked: {
    title: 'This invitation is no longer valid',
    body: 'Ask your Fruuit Consulting contact for a new invitation.',
  },
}

/**
 * Invitation landing (P-ID-010): who invited you where, then sign in with any method. A
 * user who is already signed in with the invited email can accept directly.
 */
export function InvitePage() {
  useTitle('Accept your invitation')
  const { token = '' } = useParams()
  const invite = useInviteLookup(token)
  const me = useMe()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const accept = useApiMutation((_: undefined, ctx) => acceptInvite(token, ctx), {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: meQueryKey })
      navigate('/', { replace: true })
    },
  })

  if (invite.isPending) {
    return (
      <AuthLayout>
        <div role="status" aria-label="Loading invitation" className="flex flex-col gap-3">
          <Skeleton className="h-9 w-3/4" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </AuthLayout>
    )
  }

  if (invite.isError) {
    return (
      <AuthLayout>
        <AuthHeading title="This invitation link isn't valid">
          Check that you opened the whole link from the email, or ask your Fruuit Consulting contact
          for a new invitation.
        </AuthHeading>
        <Link to="/signin" className={buttonClasses({ variant: 'secondary', fullWidth: true })}>
          Go to sign-in
        </Link>
      </AuthLayout>
    )
  }

  const data = invite.data
  const ended = ENDED[data.status]
  if (ended) {
    return (
      <AuthLayout>
        <AuthHeading title={ended.title}>{ended.body}</AuthHeading>
        <Link to="/signin" className={buttonClasses({ variant: 'secondary', fullWidth: true })}>
          Go to sign-in
        </Link>
      </AuthLayout>
    )
  }

  const summary = (
    <div className="mb-8 flex items-start gap-3 rounded-lg border border-line bg-surface p-4">
      <span
        aria-hidden="true"
        className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-wash text-teal"
      >
        <Building2 className="size-5" />
      </span>
      <div className="min-w-0 text-sm">
        <p className="font-semibold text-ink">{data.org_name}</p>
        <p className="mt-0.5 text-sage">
          {ROLE_LABELS[data.role] ?? data.role} · for {data.email_hint} · until{' '}
          {formatDate(data.expires_at)}
        </p>
      </div>
    </div>
  )

  if (me.isSuccess) {
    return (
      <AuthLayout>
        <AuthHeading title="Accept your invitation">
          You're signed in as{' '}
          <strong className="font-semibold text-ink">{me.data.user.email}</strong>.
        </AuthHeading>
        {summary}
        {accept.error ? (
          <Callout tone="danger" role="alert" className="mb-4">
            {errorMessage(accept.error)}
          </Callout>
        ) : null}
        <div className="flex flex-col gap-3">
          <Button
            size="lg"
            fullWidth
            loading={accept.isPending}
            onClick={() => accept.mutate(undefined)}
          >
            Accept and continue
          </Button>
          <Button variant="ghost" fullWidth onClick={() => void signOut()}>
            Use a different account
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <AuthHeading title="You're invited to Fruuit Engage">
        Sign in with the email address your invitation was sent to. The link works once and expires
        seven days after it was sent.
      </AuthHeading>
      {summary}
      <SignInOptions next="/" invite={token} />
    </AuthLayout>
  )
}
